/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useMemo } from 'react';

import { css, cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import Checkbox from '@atlaskit/checkbox';
import CrossIcon from '@atlaskit/icon/core/cross';
import RefreshIcon from '@atlaskit/icon/core/refresh';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import { CardClient, SmartCardProvider } from '@atlaskit/link-provider';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import Select, { type ValueType } from '@atlaskit/select';
import { Card } from '@atlaskit/smart-card';
import Spinner from '@atlaskit/spinner';
import TextArea from '@atlaskit/textarea';
import { token } from '@atlaskit/tokens';

import { messages } from '../../../common/messages';
import { getSelectedOption, type SelectOption } from '../config-panel-options';
import { FieldLabel } from '../config-panel-primitives';
import type { ManagedBlockConfigurationUpdate } from '../configuration';
import { MAX_ADDITIONAL_CONTEXT_LENGTH } from '../configuration/utils';

import type { SmartCardProviderProduct } from './smart-card-product';
import { formatSourceLabel } from './source-label';
import type {
	ManagedBlockSource,
	ManagedBlockSourceInvalidReason,
	ManagedBlockSourceMode,
} from './types';
import { useSources } from './useSources';

const sourceInputStyles = css({
	width: '100%',
	boxSizing: 'border-box',
	borderColor: token('color.border.input'),
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	borderRadius: token('radius.small'),
	backgroundColor: token('elevation.surface'),
	color: token('color.text'),
	font: 'inherit',
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
	'&::placeholder': {
		color: token('color.text.subtlest'),
	},
	'&:focus': {
		outlineColor: token('color.border.focused'),
		outlineOffset: token('space.025'),
		outlineStyle: 'solid',
		outlineWidth: token('border.width.focused'),
	},
});

const sourceInputInvalidStyles = css({
	borderColor: token('color.border.danger'),
});

const sourceChipListStyles = css({
	display: 'flex',
	flexWrap: 'wrap',
	gap: token('space.050'),
	listStyle: 'none',
	marginTop: token('space.100'),
	marginRight: 0,
	marginBottom: 0,
	marginLeft: 0,
	minWidth: 0,
	paddingTop: 0,
	paddingRight: 0,
	paddingBottom: 0,
	paddingLeft: 0,
});

const sourceChipStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	boxSizing: 'border-box',
	maxWidth: '100%',
	minWidth: 0,
	gap: token('space.050'),
	backgroundColor: token('color.background.neutral'),
	borderRadius: token('radius.small'),
	color: token('color.text.subtle'),
	paddingTop: token('space.050'),
	paddingRight: token('space.050'),
	paddingBottom: token('space.050'),
	paddingLeft: token('space.075'),
});

const sourceChipInvalidStyles = css({
	backgroundColor: token('color.background.danger'),
	borderColor: token('color.border.danger'),
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	color: token('color.text.danger'),
});

const sourceChipResolvingStyles = css({
	opacity: 0.8,
});

const sourceChipLabelStyles = css({
	minWidth: 0,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

const sourceInvalidMessageStyles = css({
	flexShrink: 0,
	paddingLeft: token('space.050'),
	whiteSpace: 'nowrap',
});

const sourceResolvedCardStyles = css({
	flexGrow: 1,
	flexShrink: 1,
	minWidth: 0,
	overflow: 'hidden',
});

const sourceStatusStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	flexGrow: 1,
	flexShrink: 1,
	gap: token('space.050'),
	minWidth: 0,
});

const errorTextStyles = css({
	color: token('color.text.danger'),
	marginTop: token('space.100'),
});

const additionalContextStyles = cssMap({
	root: {
		marginTop: token('space.100'),
	},
	textArea: {
		marginTop: token('space.075'),
	},
	message: {
		marginTop: token('space.050'),
	},
});

type SourceValidationError = 'invalid' | 'pending' | 'required' | null;

type SourcesPanelProps = {
	additionalContext: string;
	additionalContextValidationError: boolean;
	isAdditionalContextSelected: boolean;
	isDisabled?: boolean;
	onChange: (update: ManagedBlockConfigurationUpdate) => void;
	onConfigEdit: () => void;
	onInteraction: () => void;
	smartCardProviderProduct?: SmartCardProviderProduct;
	sourceEntries: ManagedBlockSource[];
	sourceMode: ManagedBlockSourceMode;
	sourceModeOptions: SelectOption<ManagedBlockSourceMode>[];
	sourceUrls: string;
	sourceValidationError: SourceValidationError;
};

type SourceItemProps = {
	isDisabled: boolean;
	onRemove: (sourceUrl: string) => void;
	onRetry: (sourceUrl: string) => void;
	source: ManagedBlockSource;
};

const getInvalidSourceMessageKey = (
	invalidReason?: ManagedBlockSourceInvalidReason,
): keyof typeof messages => {
	switch (invalidReason) {
		case 'invalid-url':
			return 'sourceInvalidUrl';
		case 'not-found':
			return 'sourceNotFound';
		case 'unauthenticated':
		case 'unauthorized':
			return 'sourceUnauthorized';
		case 'unavailable':
		default:
			return 'sourceUnavailable';
	}
};

const getSourceValidationMessageKey = (
	sourceValidationError: SourceValidationError,
): keyof typeof messages | null => {
	switch (sourceValidationError) {
		case 'invalid':
			return 'sourceInvalidError';
		case 'pending':
			return 'sourcePendingError';
		case 'required':
			return 'sourceRequiredError';
		default:
			return null;
	}
};

const SourceItem = ({ isDisabled, onRemove, onRetry, source }: SourceItemProps): JSX.Element => {
	const intl = useIntl();
	const sourceLabel = formatSourceLabel(source.url);
	const invalidMessageKey = getInvalidSourceMessageKey(source.invalidReason);

	return (
		<li
			css={[
				sourceChipStyles,
				source.status === 'invalid' && sourceChipInvalidStyles,
				source.status === 'resolving' && sourceChipResolvingStyles,
			]}
		>
			{source.status === 'resolved' && (
				<span css={sourceResolvedCardStyles}>
					<Card
						appearance="inline"
						placeholder={sourceLabel}
						resolvingPlaceholder={sourceLabel}
						url={source.url}
					/>
				</span>
			)}
			{source.status === 'resolving' && (
				<span css={sourceStatusStyles}>
					<Spinner label={intl.formatMessage(messages.sourceResolving)} size="small" />
					<span css={sourceChipLabelStyles}>{sourceLabel}</span>
				</span>
			)}
			{source.status === 'invalid' && (
				<span css={sourceStatusStyles}>
					<StatusErrorIcon label="" color="currentColor" size="small" />
					<span css={sourceChipLabelStyles}>{sourceLabel}</span>
					<span css={sourceInvalidMessageStyles}>
						<Text color="color.text.danger">{intl.formatMessage(messages[invalidMessageKey])}</Text>
					</span>
				</span>
			)}
			<Inline space="space.025" alignBlock="center">
				{source.status === 'invalid' && (
					<IconButton
						appearance="subtle"
						icon={RefreshIcon}
						isDisabled={isDisabled}
						label={intl.formatMessage(messages.retrySourceLabel, {
							source: sourceLabel,
						})}
						onClick={() => onRetry(source.url)}
						spacing="compact"
					/>
				)}
				<IconButton
					appearance="subtle"
					icon={CrossIcon}
					isDisabled={isDisabled}
					label={intl.formatMessage(messages.removeSourceLabel, {
						source: sourceLabel,
					})}
					onClick={() => onRemove(source.url)}
					spacing="compact"
				/>
			</Inline>
		</li>
	);
};

export const SourcesPanel = ({
	additionalContext,
	additionalContextValidationError,
	isDisabled = false,
	isAdditionalContextSelected,
	onChange,
	onConfigEdit,
	onInteraction,
	sourceEntries,
	sourceMode,
	sourceModeOptions,
	smartCardProviderProduct,
	sourceUrls,
	sourceValidationError,
}: SourcesPanelProps): JSX.Element => {
	const intl = useIntl();
	const cardClient = useMemo(() => new CardClient(), []);
	const {
		manualSources,
		sourceInputValue,
		handleRemoveSourceUrl,
		handleRetrySourceUrl,
		handleSourceInputChange,
		handleSourceInputKeyDown,
		handleSourceInputPaste,
		handleSourceModeChange,
		showSourceLimitError,
	} = useSources({
		cardClient,
		onChange,
		onConfigEdit,
		onInteraction,
		sourceEntries,
		sourceMode,
		sourceUrls,
	});
	const sourceErrorMessageKey = showSourceLimitError
		? 'sourceLimitError'
		: getSourceValidationMessageKey(sourceValidationError);
	const sourceErrorId = sourceErrorMessageKey ? 'agent-managed-extension-source-error' : undefined;
	const additionalContextErrorId = additionalContextValidationError
		? 'agent-managed-extension-additional-context-error'
		: undefined;
	const additionalContextCounterId = 'agent-managed-extension-additional-context-counter';
	const additionalContextDescriptionIds = [additionalContextErrorId, additionalContextCounterId]
		.filter(Boolean)
		.join(' ');
	const additionalContextCharactersRemaining = Math.max(
		0,
		MAX_ADDITIONAL_CONTEXT_LENGTH - additionalContext.length,
	);

	const handleAdditionalContextChange = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement>): void => {
			const nextAdditionalContext = event.currentTarget.value.slice(
				0,
				MAX_ADDITIONAL_CONTEXT_LENGTH,
			);

			onInteraction();

			if (additionalContext !== nextAdditionalContext) {
				onConfigEdit();
			}

			onChange((currentConfiguration) => ({
				...currentConfiguration,
				additionalContext: nextAdditionalContext,
			}));
		},
		[additionalContext, onChange, onConfigEdit, onInteraction],
	);

	const handleAdditionalContextSelectedChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>): void => {
			const nextIsAdditionalContextSelected = event.currentTarget.checked;

			onInteraction();

			if (isAdditionalContextSelected !== nextIsAdditionalContextSelected) {
				onConfigEdit();
			}

			onChange((currentConfiguration) => ({
				...currentConfiguration,
				isAdditionalContextSelected: nextIsAdditionalContextSelected,
			}));
		},
		[isAdditionalContextSelected, onChange, onConfigEdit, onInteraction],
	);

	return (
		<React.Fragment>
			<div>
				<FieldLabel htmlFor="agent-managed-extension-source-mode">
					{intl.formatMessage(messages.sourcesLabel)}
				</FieldLabel>
				<Select<SelectOption<ManagedBlockSourceMode>>
					inputId="agent-managed-extension-source-mode"
					options={sourceModeOptions}
					value={getSelectedOption(sourceModeOptions, sourceMode)}
					onChange={(option: ValueType<SelectOption<ManagedBlockSourceMode>>) => {
						if (option) {
							handleSourceModeChange(option.value);
						}
					}}
					isDisabled={isDisabled}
					isSearchable={false}
				/>
			</div>
			{sourceMode === 'manual' && (
				<div>
					<input
						aria-describedby={sourceErrorId}
						aria-invalid={Boolean(sourceErrorMessageKey)}
						css={[sourceInputStyles, sourceErrorMessageKey && sourceInputInvalidStyles]}
						data-testid="agent-managed-extension-source-input"
						disabled={isDisabled}
						onChange={handleSourceInputChange}
						onKeyDown={handleSourceInputKeyDown}
						onPaste={handleSourceInputPaste}
						placeholder={intl.formatMessage(messages.sourcesPlaceholder)}
						type="text"
						value={sourceInputValue}
					/>
					{manualSources.length > 0 && (
						<SmartCardProvider client={cardClient} product={smartCardProviderProduct}>
							<ul
								aria-label={intl.formatMessage(messages.sourceLinksLabel)}
								css={sourceChipListStyles}
							>
								{manualSources.map((source) => (
									<SourceItem
										key={source.url}
										isDisabled={isDisabled}
										onRemove={handleRemoveSourceUrl}
										onRetry={handleRetrySourceUrl}
										source={source}
									/>
								))}
							</ul>
						</SmartCardProvider>
					)}
					{sourceErrorMessageKey && (
						<div css={errorTextStyles} id={sourceErrorId} role="alert">
							<Text color="color.text.danger">
								{intl.formatMessage(messages[sourceErrorMessageKey])}
							</Text>
						</div>
					)}
				</div>
			)}
			<Box xcss={additionalContextStyles.root}>
				<Checkbox
					id="agent-managed-extension-additional-context-toggle"
					isChecked={isAdditionalContextSelected}
					isDisabled={isDisabled}
					label={intl.formatMessage(messages.additionalContextLabel)}
					onChange={handleAdditionalContextSelectedChange}
				/>
				{isAdditionalContextSelected && (
					<Box xcss={additionalContextStyles.textArea}>
						<TextArea
							aria-describedby={additionalContextDescriptionIds}
							aria-label={intl.formatMessage(messages.additionalContextDetailsLabel)}
							id="agent-managed-extension-additional-context"
							isDisabled={isDisabled}
							isInvalid={additionalContextValidationError}
							maxLength={MAX_ADDITIONAL_CONTEXT_LENGTH}
							minimumRows={3}
							onChange={handleAdditionalContextChange}
							placeholder={intl.formatMessage(messages.additionalContextPlaceholder)}
							testId="agent-managed-extension-additional-context"
							value={additionalContext}
						/>
						{additionalContextValidationError && (
							<Box
								id={additionalContextErrorId}
								role="alert"
								xcss={additionalContextStyles.message}
							>
								<Text color="color.text.danger">
									{intl.formatMessage(messages.additionalContextRequiredError)}
								</Text>
							</Box>
						)}
						<Box id={additionalContextCounterId} xcss={additionalContextStyles.message}>
							<Text color="color.text.subtle">
								{intl.formatMessage(messages.additionalContextCharactersRemaining, {
									remaining: additionalContextCharactersRemaining,
								})}
							</Text>
						</Box>
					</Box>
				)}
			</Box>
		</React.Fragment>
	);
};
